package org.example.project.screens_cus_mobile.we

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import org.example.project.dashboard.DashboardTheme
import org.example.project.screens_cus_mobile.MobileYardWEWareneingangsberichtExportHost
import org.example.project.screens_cus_mobile.MOBILE_YARD_STORED_PDF_ENABLED
import org.example.project.screens_cus_mobile.loadMobileErlaubteStandorte
import org.example.project.screens_cus_mobile.mobileStandortErlaubt
import org.example.project.service.YardApi
import org.example.project.service.YardLiveSync
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date

private const val WE_ABWEICHUNG_BRUCH = "Bruch"
private const val WE_ABWEICHUNG_FEHLMENGE = "Fehlmenge"
private const val WE_ABWEICHUNG_UEBERZAEHLIG = "Überzählig"
private const val WE_ABWEICHUNG_FEHLERFREI = "WE fehlerfrei bestätigt"

@Composable
fun CUSMobileWEWebKontrolleScreen() {
    val scope = rememberCoroutineScope()
    val refreshVersion = YardLiveSync.version()
    var loading by remember { mutableStateOf(true) }
    var auftraege by remember { mutableStateOf<List<All>>(emptyList()) }
    var selected by remember { mutableStateOf<All?>(null) }
    var berichtAuftrag by remember { mutableStateOf<All?>(null) }
    var berichtKontrolle by remember { mutableStateOf<All?>(null) }
    var berichtStatus by remember { mutableStateOf("WE Fehlerprotokoll wird erstellt") }
    var berichtProgress by remember { mutableStateOf(0) }
    var berichtFehler by remember { mutableStateOf(false) }

    suspend fun ladeDaten() {
        loading = true
        val erlaubteStandorte = loadMobileErlaubteStandorte()
        val kandidaten = YardApi.loadWeLkwListe()
            .filter { it.zustand != ZustandManager.STORNIERT }
            .filter { mobileStandortErlaubt(it, erlaubteStandorte) }
        val offen = mutableListOf<All>()
        kandidaten.forEach { auftrag ->
            val gespeichert = YardApi.loadWeWebKontrolle(auftrag, auftrag.firmenId)
            if (gespeichert?.webKontrolleAbgeschlossen != true) {
                offen += auftrag
            }
        }
        auftraege = offen.sortedWith(
            compareBy<All> { it.lieferscheinnummer }
                .thenBy { it.lfdNr }
                .thenBy { it.id }
        )
        loading = false
    }

    MobileYardWEWareneingangsberichtExportHost(
        auftrag = berichtAuftrag,
        webKontrolle = berichtKontrolle,
        onPhaseChange = { berichtStatus = it },
        onDocumentProgress = { berichtProgress = it.coerceIn(0, 100) },
        onFinished = { success ->
            if (!success) {
                berichtFehler = true
            } else {
                scope.launch {
                    berichtProgress = 100
                    berichtAuftrag = null
                    berichtKontrolle = null
                    berichtStatus = "WE Fehlerprotokoll wird erstellt"
                    berichtProgress = 0
                    berichtFehler = false
                    selected = null
                    ladeDaten()
                }
            }
        }
    )

    LaunchedEffect(refreshVersion) {
        ladeDaten()
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(16.px)
            padding(12.px)
            height(100.percent)
            minHeight(0.px)
            boxSizing("border-box")
        }
    }) {
        if (MOBILE_YARD_STORED_PDF_ENABLED && berichtAuftrag != null) {
            MobileWEWebKontrolleBerichtStatus(
                fehler = berichtFehler,
                statusText = berichtStatus,
                progress = berichtProgress,
                onOk = {
                    if (!berichtFehler) return@MobileWEWebKontrolleBerichtStatus
                    berichtAuftrag = null
                    berichtKontrolle = null
                    berichtStatus = "WE Fehlerprotokoll wird erstellt"
                    berichtProgress = 0
                    berichtFehler = false
                    selected = null
                    scope.launch { ladeDaten() }
                }
            )
        } else selected?.let { auftrag ->
            MobileWEWebKontrolleDialog(
                auftrag = auftrag,
                onZurueck = { selected = null },
                onGespeichert = { gespeichert ->
                    if (gespeichert.webKontrolleFehlerfrei) {
                        selected = null
                        scope.launch { ladeDaten() }
                    } else {
                        berichtAuftrag = auftrag
                        berichtKontrolle = gespeichert
                        berichtStatus = "WE Fehlerprotokoll wird erstellt"
                        berichtProgress = 0
                        berichtFehler = false
                        if (!MOBILE_YARD_STORED_PDF_ENABLED) selected = null
                    }
                }
            )
        } ?: run {
            MobileWEWebKontrolleHeader(offen = auftraege.size, loading = loading)
            when {
                loading -> MobileWEWebKontrolleEmpty("WE Fehlerprotokolle werden geladen...")
                auftraege.isEmpty() -> MobileWEWebKontrolleEmpty("Keine offenen WE Fehlerprotokolle vorhanden")
                else -> auftraege.forEach { auftrag ->
                    MobileWEWebKontrolleCard(auftrag = auftrag) {
                        selected = auftrag
                    }
                }
            }
        }
    }
}

@Composable
private fun MobileWEWebKontrolleDialog(
    auftrag: All,
    onZurueck: () -> Unit,
    onGespeichert: (All) -> Unit
) {
    val scope = rememberCoroutineScope()
    val vorgemeldeteAbweichungen = remember(auftrag.id, auftrag.lfdNr) { auftrag.weWebKontrolleAbweichungen() }
    var typAuswahlBestaetigt by remember(auftrag.id, auftrag.lfdNr) { mutableStateOf(vorgemeldeteAbweichungen.isNotEmpty()) }
    var ausgewaehlteTypen by remember(auftrag.id, auftrag.lfdNr) {
        mutableStateOf(vorgemeldeteAbweichungen.map { it.typ }.toSet())
    }
    var activeTyp by remember(auftrag.id, auftrag.lfdNr) { mutableStateOf("") }
    var erledigt by remember(auftrag.id, auftrag.lfdNr) { mutableStateOf<Set<String>>(emptySet()) }
    var positionen by remember(auftrag.id, auftrag.lfdNr) { mutableStateOf<List<All>>(emptyList()) }
    var nve by remember(activeTyp) { mutableStateOf("") }
    var lcode by remember(activeTyp) { mutableStateOf("") }
    var mhd by remember(activeTyp) { mutableStateOf("") }
    var artikelnummer by remember(activeTyp) { mutableStateOf("") }
    var menge by remember(activeTyp) { mutableStateOf("") }
    var error by remember(auftrag.id, auftrag.lfdNr) { mutableStateOf("") }
    var saving by remember(auftrag.id, auftrag.lfdNr) { mutableStateOf(false) }
    val abweichungen = remember(ausgewaehlteTypen, vorgemeldeteAbweichungen) {
        listOf(WE_ABWEICHUNG_BRUCH, WE_ABWEICHUNG_FEHLMENGE, WE_ABWEICHUNG_UEBERZAEHLIG)
            .filter { it in ausgewaehlteTypen }
            .map { typ ->
                vorgemeldeteAbweichungen.firstOrNull { it.typ == typ } ?: All(
                    typ = typ,
                    label = typ,
                    wert = ""
                )
            }
    }

    fun offeneTypen(): List<All> = abweichungen.filter { it.typ !in erledigt }

    fun currentPosition(): All? {
        if (activeTyp.isBlank()) return null
        if (artikelnummer.isBlank() || menge.isBlank()) {
            error = "Bitte Artikel Nummer und Menge füllen."
            return null
        }
        return All(
            typ = activeTyp,
            label = "$activeTyp ${positionen.count { it.typ == activeTyp } + 1}",
            nve = nve.trim(),
            lcode = lcode.trim(),
            mhd = mhd.trim(),
            artikelNummer = artikelnummer.trim(),
            menge = menge.trim()
        )
    }

    fun clearPosition() {
        nve = ""
        lcode = ""
        mhd = ""
        artikelnummer = ""
        menge = ""
        error = ""
    }

    suspend fun speichern(finalPositionen: List<All>) {
        saving = true
        val kontrolle = All(
            id = auftrag.id.ifBlank { auftrag.lfdNr.toString() },
            lfdNr = auftrag.lfdNr,
            lieferscheinnummer = auftrag.lieferscheinnummer,
            auftragsart = "WE",
            typ = "WE_WEB_KONTROLLE",
            label = "WE Fehlerprotokoll ${auftrag.lieferscheinnummer}",
            kontrollPositionen = finalPositionen,
            module = abweichungen,
            webKontrolleAbgeschlossen = true,
            webKontrolleFehlerfrei = finalPositionen.isEmpty() && WE_ABWEICHUNG_FEHLERFREI in ausgewaehlteTypen,
            webKontrolleAbgeschlossenAm = aktuelleWEWebKontrolleDatum(),
            webKontrolleAbgeschlossenUm = aktuelleWEWebKontrolleUhrzeit(),
            abgeschlossenAm = aktuelleWEWebKontrolleDatum(),
            abgeschlossenUm = aktuelleWEWebKontrolleUhrzeit(),
            mobilWePalettenBruch = auftrag.weWebKontrolleExtra(finalPositionen)["mobil.wePalettenBruch"].orEmpty().toIntOrNull() ?: 0,
            mobilWePalettenUeberzaehlig = auftrag.weWebKontrolleExtra(finalPositionen)["mobil.wePaletten\u00DCberz\u00E4hlig"].orEmpty().toIntOrNull() ?: 0,
            mobilWePalettenFehlmenge = auftrag.weWebKontrolleExtra(finalPositionen)["mobil.wePalettenFehlmenge"].orEmpty().toIntOrNull() ?: 0,
            count = finalPositionen.size,
            palettenWerte = auftrag.weWebKontrolleExtra(finalPositionen)
        )
        val gespeichert = YardApi.saveWeWebKontrolle(auftrag, kontrolle, auftrag.firmenId) ?: kontrolle
        saving = false
        onGespeichert(gespeichert)
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(14.px)
            height(100.percent)
            minHeight(0.px)
        }
    }) {
        MobileWEWebKontrolleCard(auftrag = auftrag, onOpen = {})

        if (!typAuswahlBestaetigt) {
            H3({ style { margin(0.px); color(DashboardTheme.TEXT_LIGHT); fontSize(18.px); textAlign("center") } }) {
                Text("Welche Fehler wurden gefunden?")
            }
            listOf(WE_ABWEICHUNG_BRUCH, WE_ABWEICHUNG_FEHLMENGE, WE_ABWEICHUNG_UEBERZAEHLIG, WE_ABWEICHUNG_FEHLERFREI).forEach { typ ->
                MobileWEWebKontrolleTypCheckbox(
                    label = typ,
                    checked = typ in ausgewaehlteTypen,
                    onChange = { checked ->
                        ausgewaehlteTypen = when {
                            checked && typ == WE_ABWEICHUNG_FEHLERFREI -> setOf(WE_ABWEICHUNG_FEHLERFREI)
                            checked -> (ausgewaehlteTypen - WE_ABWEICHUNG_FEHLERFREI) + typ
                            else -> ausgewaehlteTypen - typ
                        }
                        error = ""
                    }
                )
            }
            if (error.isNotBlank()) {
                Span({ style { color(DashboardTheme.ACCENT); fontSize(13.px); fontWeight("700"); textAlign("center") } }) {
                    Text(error)
                }
            }
            Div({ style { display(DisplayStyle.Flex); gap(10.px); width(100.percent) } }) {
                Div({ style { flex(1) } }) {
                    LumaticaButton("Zurück", onZurueck, variant = LumaticaButtonVariant.Standard, heightPx = 42, enabled = !saving)
                }
                Div({ style { flex(1) } }) {
                    LumaticaButton(
                        "Weiter",
                        onClick = {
                            if (ausgewaehlteTypen.isEmpty()) {
                                error = "Bitte mindestens eine Fehlerart auswählen."
                            } else if (WE_ABWEICHUNG_FEHLERFREI in ausgewaehlteTypen) {
                                scope.launch { speichern(emptyList()) }
                            } else {
                                typAuswahlBestaetigt = true
                                error = ""
                            }
                        },
                        variant = LumaticaButtonVariant.Action,
                        heightPx = 42,
                        enabled = !saving
                    )
                }
            }
        } else if (activeTyp.isBlank()) {
            H3({ style { margin(0.px); color(DashboardTheme.TEXT_LIGHT); fontSize(18.px); textAlign("center") } }) {
                Text("Was möchtest du melden?")
            }
            offeneTypen().forEach { abweichung ->
                LumaticaButton(
                    label = "${abweichung.typ}: ${abweichung.wert}",
                    onClick = {
                        activeTyp = abweichung.typ
                        clearPosition()
                    },
                    variant = LumaticaButtonVariant.State,
                    isActive = true,
                    heightPx = 42
                )
            }
            if (offeneTypen().isEmpty()) {
                MobileWEWebKontrolleEmpty("Alle Abweichungen wurden erfasst.")
            }
            Div({ style { display(DisplayStyle.Flex); gap(10.px); width(100.percent) } }) {
                Div({ style { flex(1) } }) {
                    LumaticaButton(
                        "Zurück",
                        onClick = {
                            if (vorgemeldeteAbweichungen.isEmpty()) {
                                typAuswahlBestaetigt = false
                                erledigt = emptySet()
                                activeTyp = ""
                                error = ""
                            } else {
                                onZurueck()
                            }
                        },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42,
                        enabled = !saving
                    )
                }
                Div({ style { flex(1) } }) {
                    LumaticaButton(
                        if (saving) "Speichere..." else "WE Fehlerprotokoll speichern",
                        onClick = {
                            if (offeneTypen().isNotEmpty()) {
                                error = "Bitte alle offenen Abweichungen abschließen."
                                return@LumaticaButton
                            }
                            scope.launch { speichern(positionen) }
                        },
                        variant = LumaticaButtonVariant.Action,
                        heightPx = 42,
                        enabled = !saving && offeneTypen().isEmpty()
                    )
                }
            }
        } else {
            H3({ style { margin(0.px); color(DashboardTheme.TEXT_LIGHT); fontSize(18.px); textAlign("center") } }) {
                Text("$activeTyp Position")
            }
            MobileWEWebKontrolleInput("NVE / Charge", nve) { nve = it; error = "" }
            MobileWEWebKontrolleInput("L-Code", lcode) { lcode = it; error = "" }
            MobileWEWebKontrolleInput("MHD", mhd) { mhd = it; error = "" }
            MobileWEWebKontrolleInput("Artikel Nummer", artikelnummer) { artikelnummer = it; error = "" }
            MobileWEWebKontrolleInput("Menge", menge, numberOnly = true) { menge = it; error = "" }
            if (error.isNotBlank()) {
                Span({ style { color(DashboardTheme.ACCENT); fontSize(13.px); fontWeight("700"); textAlign("center") } }) {
                    Text(error)
                }
            }
            if (positionen.any { it.typ == activeTyp }) {
                Span({ style { color(DashboardTheme.TEXT_MUTED); fontSize(13.px); fontWeight("700"); textAlign("center") } }) {
                    Text("${positionen.count { it.typ == activeTyp }} Position(en) erfasst")
                }
            }
            Div({ style { display(DisplayStyle.Flex); gap(10.px); width(100.percent) } }) {
                Div({ style { flex(1) } }) {
                    LumaticaButton(
                        "Position hinzufügen",
                        onClick = {
                            val pos = currentPosition() ?: return@LumaticaButton
                            positionen = positionen + pos
                            clearPosition()
                        },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42,
                        enabled = !saving
                    )
                }
                Div({ style { flex(1) } }) {
                    LumaticaButton(
                        "Diese Position abschließen",
                        onClick = {
                            val pos = currentPosition() ?: return@LumaticaButton
                            positionen = positionen + pos
                            erledigt = erledigt + activeTyp
                            activeTyp = ""
                            clearPosition()
                        },
                        variant = LumaticaButtonVariant.Action,
                        heightPx = 42,
                        enabled = !saving
                    )
                }
            }
        }
    }
}

@Composable
private fun MobileWEWebKontrolleBerichtStatus(
    fehler: Boolean,
    statusText: String,
    progress: Int,
    onOk: () -> Unit
) {
    Div({
        style {
            padding(18.px)
            borderRadius(8.px)
            background("linear-gradient(180deg, rgba(255,0,0,0.14) 0%, rgba(255,255,255,0.03) 100%), ${DashboardTheme.PREMIUM_SURFACE_BG}")
            property("border", "1px solid rgba(255,0,0,0.45)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(14.px)
            textAlign("center")
        }
    }) {
        H3({ style { margin(0.px); color(DashboardTheme.TEXT_LIGHT); fontSize(20.px) } }) {
            Text(if (fehler) "Dokument konnte nicht erzeugt werden" else statusText)
        }
        Span({ style { color(DashboardTheme.TEXT_MUTED); fontSize(14.px); fontWeight("700") } }) {
            Text("${progress.coerceIn(0, 100)}%")
        }
        Div({
            style {
                width(100.percent)
                height(8.px)
                borderRadius(8.px)
                backgroundColor(Color("rgba(255,255,255,0.08)"))
                property("overflow", "hidden")
            }
        }) {
            Div({
                style {
                    width(progress.coerceIn(0, 100).percent)
                    height(100.percent)
                    backgroundColor(DashboardTheme.ACCENT)
                }
            })
        }
        if (fehler) {
            LumaticaButton("OK", onOk, variant = LumaticaButtonVariant.Action, heightPx = 42)
        }
    }
}

@Composable
private fun MobileWEWebKontrolleHeader(offen: Int, loading: Boolean) {
    Div({
        style {
            padding(18.px)
            borderRadius(8.px)
            background("linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%), ${DashboardTheme.PREMIUM_SURFACE_BG}")
            property("border", "1px solid rgba(255,255,255,0.10)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            gap(12.px)
            textAlign("center")
        }
    }) {
        H3({ style { margin(0.px); color(DashboardTheme.TEXT_LIGHT); fontSize(20.px) } }) {
            Text("WE Fehlerprotokoll")
        }
        Span({ style { color(DashboardTheme.TEXT_LIGHT); fontSize(24.px); fontWeight("800") } }) {
            Text(if (loading) "..." else "$offen offen")
        }
    }
}

@Composable
private fun MobileWEWebKontrolleCard(auftrag: All, onOpen: () -> Unit) {
    Div({
        style {
            padding(16.px)
            borderRadius(8.px)
            backgroundColor(DashboardTheme.SURFACE_CARD)
            property("border", "1px solid rgba(255,255,255,0.08)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(10.px)
            property("box-shadow", "0 12px 28px rgba(0,0,0,0.18)")
            cursor("pointer")
        }
        onClick { onOpen() }
    }) {
        MobileWEWebKontrolleInfoRow("LS Nummer", auftrag.lieferscheinnummer.ifBlank { "-" })
        MobileWEWebKontrolleInfoRow("Status", ZustandManager.textFor(auftrag.zustand))
        MobileWEWebKontrolleInfoRow("Bruch", auftrag.mobilWePalettenBruch.takeIf { it > 0 }?.toString().orEmpty().ifBlank { "0" })
        MobileWEWebKontrolleInfoRow("Überzählig", auftrag.mobilWePalettenUeberzaehlig.takeIf { it > 0 }?.toString().orEmpty().ifBlank { "0" })
        MobileWEWebKontrolleInfoRow("Fehlmenge", auftrag.mobilWePalettenUeberzaehlig.takeIf { it > 0 }?.toString().orEmpty().ifBlank { "0" })
    }
}

@Composable
private fun MobileWEWebKontrolleInput(label: String, value: String, numberOnly: Boolean = false, onChange: (String) -> Unit) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(7.px); width(100.percent) } }) {
        Span({ style { color(DashboardTheme.TEXT_LIGHT); fontSize(14.px); fontWeight("800") } }) { Text(label) }
        Input(if (numberOnly) InputType.Number else InputType.Text) {
            value(value)
            attr("placeholder", label)
            style {
                height(52.px)
                width(100.percent)
                padding(0.px, 14.px)
                color(DashboardTheme.TEXT_LIGHT)
                backgroundColor(Color("rgba(255,255,255,0.04)"))
                property("border", "1px solid rgba(255,255,255,0.14)")
                borderRadius(8.px)
                property("box-sizing", "border-box")
                fontSize(17.px)
                fontWeight("800")
            }
            onInput { event ->
                val raw = event.value?.toString().orEmpty()
                onChange(if (numberOnly) raw.filter(Char::isDigit) else raw)
            }
        }
    }
}

@Composable
private fun MobileWEWebKontrolleInfoRow(label: String, value: String) {
    Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); gap(10.px) } }) {
        Span({ style { color(DashboardTheme.TEXT_MUTED); fontSize(13.px); fontWeight("700") } }) { Text(label) }
        Span({ style { color(DashboardTheme.TEXT_LIGHT); fontSize(14.px); fontWeight("800"); textAlign("right") } }) { Text(value) }
    }
}

@Composable
private fun MobileWEWebKontrolleTypCheckbox(label: String, checked: Boolean, onChange: (Boolean) -> Unit) {
    Div({
        style {
            padding(14.px)
            borderRadius(8.px)
            backgroundColor(DashboardTheme.SURFACE_CARD)
            property("border", "1px solid rgba(255,255,255,0.08)")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            gap(12.px)
            boxSizing("border-box")
        }
    }) {
        Span({ style { color(DashboardTheme.TEXT_LIGHT); fontSize(16.px); fontWeight("800") } }) {
            Text(label)
        }
        LumaticaCheckbox(checked = checked, onChange = onChange)
    }
}

@Composable
private fun MobileWEWebKontrolleEmpty(text: String) {
    Div({
        style {
            padding(18.px)
            borderRadius(8.px)
            backgroundColor(DashboardTheme.SURFACE_CARD)
            property("border", "1px solid rgba(255,255,255,0.08)")
            color(DashboardTheme.TEXT_MUTED)
            fontSize(14.px)
            fontWeight("700")
            textAlign("center")
        }
    }) {
        Text(text)
    }
}

private fun All.weWebKontrolleAbweichungen(): List<All> =
    listOf(
        WE_ABWEICHUNG_BRUCH to mobilWePalettenBruch,
        WE_ABWEICHUNG_UEBERZAEHLIG to mobilWePalettenUeberzaehlig,
        WE_ABWEICHUNG_FEHLMENGE to mobilWePalettenFehlmenge
    )
        .filter { (_, wert) -> wert > 0 }
        .map { (typ, wert) ->
            All(
                typ = typ,
                label = typ,
                wert = wert.toString()
            )
        }

private fun All.weWebKontrolleExtra(positionen: List<All>): Map<String, String> =
    mapOf(
        "lieferscheinnummer" to lieferscheinnummer,
        "mobil.wePalettenBruch" to weWebKontrolleWertOderPositionen("mobil.wePalettenBruch", WE_ABWEICHUNG_BRUCH, positionen),
        "mobil.wePalettenÜberzählig" to weWebKontrolleWertOderPositionen("mobil.wePalettenÜberzählig", WE_ABWEICHUNG_UEBERZAEHLIG, positionen),
        "mobil.wePalettenFehlmenge" to weWebKontrolleWertOderPositionen("mobil.wePalettenFehlmenge", WE_ABWEICHUNG_FEHLMENGE, positionen)
    )

private fun All.weWebKontrolleWertOderPositionen(key: String, typ: String, positionen: List<All>): String =
    when (typ) {
        WE_ABWEICHUNG_BRUCH -> mobilWePalettenBruch.toString()
        WE_ABWEICHUNG_UEBERZAEHLIG -> mobilWePalettenUeberzaehlig.toString()
        WE_ABWEICHUNG_FEHLMENGE -> mobilWePalettenFehlmenge.toString()
        else -> ""
    }.takeIf { it.isNotBlank() && it != "0" }
        ?: positionen.count { it.typ == typ }.toString()

private fun aktuelleWEWebKontrolleDatum(): String {
    val date = Date()
    return "${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}"
}

private fun aktuelleWEWebKontrolleUhrzeit(): String {
    val date = Date()
    return "${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}"
}
